import { AxiosResponse } from 'axios';
import { observable, runInAction } from 'mobx';
import ErrorUtil from '../error/ErrorUtil';
import { CommonAPI } from '../util/ApiUtil';
import { FetchStatus } from '../util/FetchStatus';

interface UsedOrder {
  dateTime: string;
  customerId: number;
  orderNumberId: number;
}

interface DiscountCode {
  discountCode: string;
  isUsable: boolean;
  lastUsedOrder: UsedOrder;
}

export interface DiscountCodeData extends DiscountCode {
  redeemed: boolean;
  used: boolean;
  fetchStatus: FetchStatus;
}

class DiscountCodeStore {
  @observable accessor discountCodeMap: Map<string, DiscountCodeData> = new Map<string, DiscountCodeData>();

  public async createDiscountCode(salesCampaignId: number, recommendationItemId: number) {
    const discountCode = await this.resolveWithStatus(
      CommonAPI.post('/discountcode', {
        salesCampaignId,
        recommendationItemId,
      }),
      this.getDiscountCodeData(salesCampaignId, recommendationItemId),
    );
    this.updateDiscountCodeStatus(salesCampaignId, recommendationItemId, discountCode);
  }

  public async fetchDiscountCode(salesCampaignId: number, recommendationItemId: number) {
    const discountCodeData = this.getDiscountCodeData(salesCampaignId, recommendationItemId);
    if (discountCodeData.discountCode || [FetchStatus.FETCHING, FetchStatus.FETCHED].includes(discountCodeData.fetchStatus)) {
      return;
    }
    const discountCode = await this.resolveWithStatus(
      CommonAPI.get('/discountcode', {
        params: { salesCampaignId, recommendationItemId },
      }),
      discountCodeData,
    );
    this.updateDiscountCodeStatus(salesCampaignId, recommendationItemId, discountCode);
  }

  public getDiscountCodeData(salesCampaignId: number, recommendationItemId: number) {
    const key = this.getMapKey(salesCampaignId, recommendationItemId);
    if (!this.discountCodeMap.has(key)) {
      this.setDiscountCodeData(salesCampaignId, recommendationItemId);
    }
    return this.discountCodeMap.get(key);
  }

  public setDiscountCodeData(salesCampaignId: number, recommendationItemId: number, discountCodeData?: Partial<DiscountCodeData>) {
    const key = this.getMapKey(salesCampaignId, recommendationItemId);
    runInAction(() => {
      this.discountCodeMap.set(
        key,
        observable({
          ...this.getDefaultData(),
          ...this.discountCodeMap.get(key),
          ...discountCodeData,
        }),
      );
    });
  }

  private updateDiscountCodeStatus(salesCampaignId: number, recommendationItemId: number, discountCode: DiscountCode) {
    const discountCodeStatus = this.getUpdatedDiscountCodeStatus(discountCode);
    this.setDiscountCodeData(salesCampaignId, recommendationItemId, {
      ...discountCode,
      ...discountCodeStatus,
    });
  }

  private getUpdatedDiscountCodeStatus(discountCode: DiscountCode) {
    if (discountCode) {
      return {
        redeemed: true,
        used: !!discountCode.lastUsedOrder,
      };
    }
  }

  private getMapKey(salesCampaignId: number, recommendationItemId: number) {
    return `${salesCampaignId}_${recommendationItemId}`;
  }

  private getDefaultData(): DiscountCodeData {
    return {
      fetchStatus: FetchStatus.UNINITIALIZED,
      used: false,
      redeemed: false,
      discountCode: null,
      isUsable: false,
      lastUsedOrder: null,
    };
  }

  private async resolveWithStatus(requestPromise: Promise<AxiosResponse<DiscountCode>>, discountCodeData: DiscountCodeData): Promise<DiscountCode> {
    try {
      discountCodeData.fetchStatus = FetchStatus.FETCHING;
      const discountCode = (await requestPromise).data;
      discountCodeData.fetchStatus = FetchStatus.FETCHED;
      return discountCode;
    } catch (e) {
      ErrorUtil.pushErrorToTeliaRumAndLog(e as Error);
      discountCodeData.fetchStatus = FetchStatus.ERROR;
    }
  }
}

export default DiscountCodeStore;
